import { avatarHueIndex, avatarInitials } from '@papan/types'

/**
 * Shared avatar scheme — ONE source so every avatar (the taskbar AvatarMenu,
 * the WhoHasAccess audit dialog, the share-links manager, …) renders the same
 * 2-letter initials on the same name-derived color. A given identity always
 * maps to the same color across re-renders and surfaces.
 *
 * Originally lived in features/share-links; promoted here so shared/core-ui
 * (which can't import from features/*) can use it too — share-links now
 * re-exports from this module.
 */

/** SEMANTIC — DO NOT BRAND. The palette is THE avatar scheme in `@papan/types` (avatar.ts): sixteen
 *  hues, each a soft gradient into its neighbour, so different people never look alike and one
 *  person looks the same on every surface (here, the splash, the identity bars). These are the same
 *  gradients as Tailwind classes, IN THE SAME ORDER — written out literally so Tailwind generates
 *  them. `avatar-scheme-parity.test.ts` pins the two together. */
export const AVATAR_PALETTE = [
  'bg-gradient-to-br from-red-400 to-rose-500',       'bg-gradient-to-br from-orange-400 to-red-500',
  'bg-gradient-to-br from-amber-400 to-orange-500',   'bg-gradient-to-br from-lime-400 to-green-500',
  'bg-gradient-to-br from-green-400 to-emerald-500',  'bg-gradient-to-br from-emerald-400 to-teal-500',
  'bg-gradient-to-br from-teal-400 to-cyan-500',      'bg-gradient-to-br from-cyan-400 to-sky-500',
  'bg-gradient-to-br from-sky-400 to-blue-500',       'bg-gradient-to-br from-blue-400 to-indigo-500',
  'bg-gradient-to-br from-indigo-400 to-purple-400',  'bg-gradient-to-br from-violet-400 to-purple-500',
  'bg-gradient-to-br from-purple-400 to-fuchsia-500', 'bg-gradient-to-br from-fuchsia-400 to-pink-500',
  'bg-gradient-to-br from-pink-400 to-rose-500',      'bg-gradient-to-br from-rose-400 to-red-500',
] as const

/** Stable Tailwind background for a seed (email / id / name) — the same seed always maps to the
 *  same gradient, so an identity is recognizable at a glance across surfaces. The seed is
 *  case-folded (in `avatarHueIndex`), and that is load-bearing: the same person reaches this through
 *  fields that disagree on case, and one differing letter would pick another colour. */
export function avatarBg(seed: string): string {
  return AVATAR_PALETTE[avatarHueIndex(seed)]!
}

/** Two-letter initials from a name OR email — THE rule, `avatarInitials` in `@papan/types`. */
export const getInitials = avatarInitials

/** Display name derived from an email's local-part — "alice.smith@x" →
 *  "Alice Smith". For surfaces with no display-name registry (share-link
 *  recipients). */
export function nameFromEmail(email: string): string {
  const local = email.split('@')[0] || email
  return local
    .split(/[\s_.\-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ') || email
}

/** Back-compat alias for `getInitials` (which also handles emails). */
export const initialFromEmail = getInitials
